import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "../../components";
import { sendContact } from "../../api/apiService";
import { MODAL_TYPES } from "../../utils/modal.utils";
import { FormValidation } from "../Validation/FormValidation";
import type {
  FormContactPayload,
  FormContactProps,
} from "../../types/form.types";
import type { FormikHelpers } from "formik";
import { useServiceData } from "../../context";
import styles from "./Form.module.css";

type ReCaptcha = string | null;

export const FormContainer = () => {
  const { t } = useTranslation();
  const { reasons, departments, cities } = useServiceData();

  const [recaptchaToken, setRecaptchaToken] = React.useState<ReCaptcha>(null);
  const [activeModal, setActiveModal] = React.useState<
    keyof typeof MODAL_TYPES | null
  >(null);

  const closeModal = () => setActiveModal(null);

  const renderModal = React.useMemo(() => {
    if (!activeModal) return null;
    const { title, icon, message } = MODAL_TYPES[activeModal];

    return (
      <Modal
        isOpen={!!activeModal}
        onClose={closeModal}
        title={t(title)}
        icon={icon}
      >
        <div className={styles.textCenter}>
          <p className={styles.textMuted}>{t(message)}</p>
          <Button type="button" onClick={closeModal}>
            {t("modal.button")}
          </Button>
        </div>
      </Modal>
    );
  }, [t, activeModal]);

  const handleSubmitData = async (
    values: FormContactProps,
    { setSubmitting, resetForm }: FormikHelpers<any>
  ) => {
    if (!recaptchaToken) {
      alert(t("form.recaptcha.invalid"));
      return;
    }

    try {
      const payload: FormContactPayload = {
        ...values,
        department: departments.find((d) => d.id === values.department),
        city: cities.find((c) => c.code === values.city),
        contactReason: reasons.find((r) => r.code === values.contactReason),
        attachment: values.attachment || {},
      };

      const { data } = await sendContact(payload);
      setActiveModal(data.code === "200" ? "success" : "failure");
    } catch (error) {
      console.error(error);
      setActiveModal("failure");
    } finally {
      setSubmitting(false);
      resetForm();
    }
  };

  return (
    <div className={styles.formContainer}>
      <p className={styles.formSubtitle}>{t("form.subtitle")}</p>
      <FormValidation
        onSubmit={handleSubmitData}
        recaptchaToken={recaptchaToken}
        onRecaptchaChange={setRecaptchaToken}
      />

      {renderModal}
    </div>
  );
};
